import React, { useState } from 'react';
import { Phone, Mail, MapPin, Send, Clock, MessageSquare, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    projectType: '',
    message: '',
    phone: ''
  });
  const [formStatus, setFormStatus] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus('sending');
    
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setFormStatus('sent');
        setFormData({
          name: '',
          company: '',
          projectType: '',
          message: '',
          phone: ''
        });
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again or contact us directly.');
      setFormStatus('');
    }
  };

  const projectTypes = [
    'Solar EPC',
    'Wind EPC',
    'BESS / Energy Storage',
    'Substation & Switchyard',
    'HT/LT Electrical Installation',
    'Transmission Line',
    'Control & Instrumentation (C&I)',
    'Plant & Machinery Erection',
    'O&M Services',
    'Other'
  ];

  return (
    <div className="min-h-screen pt-32 pb-20">
      <SEO 
        title="Contact Us | Calspark Electrical & Engineers"
        description="Get in touch with Calspark Electrical & Engineers. Based in Bangalore South, Karnataka. Reach us for EPC project enquiries, partnerships and recruitment at projects@calsparkengineers.com"
        keywords="contact Calspark, EPC contractor Bangalore, electrical engineers Karnataka, Pan India EPC enquiry"
        ogUrl="/contact"
      />
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-800 to-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6">Contact Us</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Let's discuss your project requirements. Our team is ready to help you with expert EPC solutions.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 -mt-20 relative z-10">
            <div className="bg-white rounded-xl shadow-xl p-8 border border-slate-200 hover:shadow-2xl transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                <Phone className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Call Us</h3>
              <p className="text-slate-600 mb-4">Speak directly with our team</p>
              <a href="tel:+918147451515" className="text-teal-600 font-medium hover:text-teal-700 block">
                8147451515
              </a>
            </div>

            <div className="bg-white rounded-xl shadow-xl p-8 border border-slate-200 hover:shadow-2xl transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                <Mail className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">Email Us</h3>
              <p className="text-slate-600 mb-4">Send us your inquiry</p>
              <a href="mailto:enquiry@calsparkengineers.com" className="text-teal-600 font-medium hover:text-teal-700 block text-base">
                enquiry@calsparkengineers.com
              </a>
            </div>

            <div className="bg-white rounded-xl shadow-xl p-8 border border-slate-200 hover:shadow-2xl transition-shadow">
              <div className="w-14 h-14 bg-teal-100 rounded-xl flex items-center justify-center mb-4">
                <MessageSquare className="w-7 h-7 text-teal-600" />
              </div>
              <h3 className="text-xl font-semibold text-slate-800 mb-3">WhatsApp</h3>
              <p className="text-slate-600 mb-4">Quick response via WhatsApp</p>
              <a
                href="https://wa.me/918147451515"
                target="_blank"
                rel="noopener noreferrer"
                className="text-teal-600 font-medium hover:text-teal-700"
              >
                Chat on WhatsApp
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
              <h2 className="text-3xl font-bold text-slate-800 mb-6">Send Us a Message</h2>
              <p className="text-slate-600 mb-8">
                Fill out the form below and our team will get back to you within 4 business hours.
              </p>

              {formStatus === 'sent' ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-20 h-20 text-teal-600 mx-auto mb-6" />
                  <h3 className="text-2xl font-bold text-slate-800 mb-3">Thank You!</h3>
                  <p className="text-slate-600 text-lg">We respond to all inquiries within 4 business hours. Your information is kept confidential.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                        placeholder="Your name"
                      />
                    </div>

                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-slate-700 mb-2">
                        Company Name
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                        placeholder="Company name"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="projectType" className="block text-sm font-medium text-slate-700 mb-2">
                        Project Type *
                      </label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                      >
                        <option value="">Select project type</option>
                        {projectTypes.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2">
                        Phone / WhatsApp *
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors"
                        placeholder="Your phone number"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-2">
                      Message *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-colors resize-none"
                      placeholder="Tell us about your project requirements..."
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={formStatus === 'sending'}
                    className="w-full bg-teal-600 text-white px-6 py-4 rounded-lg font-medium hover:bg-teal-700 transition-all duration-300 shadow-lg hover:shadow-xl flex items-center justify-center gap-2 disabled:opacity-70"
                  >
                    {formStatus === 'sending' ? (
                      'Sending...'
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Send Message
                      </>
                    )}
                  </button>

                  {/* Trust Signal */}
                  <p className="text-center text-sm text-slate-500 pt-2">
                    We respond to all inquiries within 4 business hours. Your information is kept confidential.
                  </p>
                </form>
              )}
            </div>

            {/* Map & Office Info */}
            <div className="space-y-8">
              {/* Office Details */}
              <div className="bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
                <h3 className="text-2xl font-bold text-slate-800 mb-6">Office Location</h3>
                
                <div className="space-y-6">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">Address</h4>
                      <p className="text-slate-600">
                        #684, Sathnoor Road, Channapatna<br />
                        Bangalore South - 562160
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-slate-800 mb-1">Business Hours</h4>
                      <p className="text-slate-600">
                        Monday - Saturday: 10:00 AM - 6:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Google Maps Embed */}
              <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200">
                <iframe
                  title="Calspark Office Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3890.2!2d77.2!3d12.65!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTLCsDM5JzAwLjAiTiA3N8KwMTInMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890"
                  width="100%"
                  height="300"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  className="w-full"
                ></iframe>
                <div className="p-4 bg-slate-50">
                  <a
                    href="https://www.google.com/maps/search/#684,+Sathnoor+Road,+Channapatna,+Bangalore+South"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-teal-600 font-medium hover:text-teal-700 text-sm"
                  >
                    Open in Google Maps →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
